import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Typewriter } from "react-simple-typewriter";
import headshot from "../assets/images/headshot.webp";
import resume from "../assets/resume/resume.pdf";

// ── Palette ──────────────────────────────────────────
const CHARCOAL = "#111111";
const GOLD = "#C4A25D";
const GOLD_HOVER = "#B08E48";
const GOLD_LIGHT = "#E8D5AA";
const WARM_WHITE = "#F5F0E8";
const MUTED = "#9E9E8E";

// ── Typewriter words ──────────────────────────────────
const WORDS = [
  "I help organizations get their communications in order.",
  "I build PR programs that keep working even when you're not in the room.",
  "I turn scattered messaging into something your team can actually use.",
  "I help teams get ahead of the story instead of chasing it.",
  "I'd rather help you plan for a crisis than manage one for you.",
  "I figure out the strategy and help you put it into practice.",
  "Good communication isn't luck — it's something you build.",
  "I've worked in government, nonprofits, and advocacy for 15+ years.",
];

// ── Animation variants ────────────────────────────────
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.18 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.9, ease: "easeOut" } },
};

// ─────────────────────────────────────────────────────
export default function HomeHero() {
  return (
    <HeroSection>
      {/* ── Left: Text panel ── */}
      <TextPanel
        as={motion.div}
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={fadeUp}>
          <Overline>Buffalo, NY &nbsp;·&nbsp; PR · Consulting · Communications</Overline>
        </motion.div>

        <motion.div variants={fadeUp}>
          <HeroName>Jim<br />Montour</HeroName>
        </motion.div>

        <GoldRule as={motion.div} variants={fadeUp} />

        <motion.div variants={fadeUp}>
          <ModesLine>
            PR Specialist &nbsp;·&nbsp; Fractional Consultant &nbsp;·&nbsp; Development Leader
          </ModesLine>
        </motion.div>

        <motion.div variants={fadeIn} style={{ minHeight: "3.5rem" }}>
          <TypedLine>
            <Typewriter
              words={WORDS}
              loop={false}
              cursor
              cursorStyle="_"
              typeSpeed={38}
              deleteSpeed={22}
              delaySpeed={1800}
            />
          </TypedLine>
        </motion.div>

        <motion.div variants={fadeUp}>
          <ButtonRow>
            <PrimaryBtn href={resume} target="_blank" rel="noopener noreferrer">
              View Resume
            </PrimaryBtn>
            <GhostBtn as={Link} to="/contact">
              Work With Me
            </GhostBtn>
          </ButtonRow>
        </motion.div>

      </TextPanel>

      {/* ── Right: Photo panel ── */}
      <PhotoPanel
        as={motion.div}
        initial={{ opacity: 0, scale: 1.04 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease: "easeOut", delay: 0.2 }}
      >
        <img src={headshot} alt="Jim Montour" fetchpriority="high" />
        <PhotoOverlay />
      </PhotoPanel>
    </HeroSection>
  );
}

// ── Styled Components ─────────────────────────────────

const grain = keyframes`
  0%, 100% { transform: translate(0, 0); }
  10%       { transform: translate(-2%, -3%); }
  30%       { transform: translate(2%, 2%); }
  50%       { transform: translate(-1%, 3%); }
  70%       { transform: translate(3%, -1%); }
  90%       { transform: translate(-2%, 1%); }
`;

const HeroSection = styled.section`
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    flex-direction: column;
    min-height: auto;
  }
`;

const TextPanel = styled.div`
  flex: 0 0 56%;
  background: ${CHARCOAL};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  padding: 5rem 5rem 5rem 6vw;
  position: relative;
  overflow: hidden;

  /* Subtle noise texture */
  &::after {
    content: "";
    position: absolute;
    inset: -50%;
    width: 200%;
    height: 200%;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity: 0.025;
    animation: ${grain} 8s steps(1) infinite;
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 900px) {
    flex: none;
    padding: 3rem 1.25rem 2.5rem;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1rem 2rem;
  }
`;

const Overline = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${GOLD};
  opacity: 0.85;
`;

const HeroName = styled.h1`
  font-family: 'DM Serif Display', Georgia, serif;
  font-weight: 400;
  font-size: clamp(3.5rem, 7vw, 6rem);
  line-height: 1.0;
  color: ${WARM_WHITE};
  letter-spacing: -0.02em;
  margin: 0;
`;

const GoldRule = styled.div`
  width: 48px;
  height: 2px;
  background: ${GOLD};
  border-radius: 1px;
`;

const ModesLine = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.825rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: ${MUTED};
  margin: 0;
  line-height: 1.5;
`;

const TypedLine = styled.p`
  font-family: 'DM Serif Display', Georgia, serif;
  font-style: italic;
  font-size: clamp(1rem, 1.8vw, 1.25rem);
  color: ${GOLD_LIGHT};
  line-height: 1.55;
  margin: 0;
  min-height: 3.5rem;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.875rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const PrimaryBtn = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.8rem 1.75rem;
  background: ${GOLD};
  color: ${CHARCOAL};
  border-radius: 0.375rem;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: ${GOLD_HOVER};
    color: ${CHARCOAL};
    transform: translateY(-2px);
  }
`;

const GhostBtn = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.8rem 1.75rem;
  background: transparent;
  color: ${WARM_WHITE};
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  border-radius: 0.375rem;
  text-decoration: none;
  letter-spacing: 0.01em;
  transition: border-color 0.2s, color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${GOLD};
    color: ${GOLD};
    transform: translateY(-2px);
  }
`;

const PhotoPanel = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  min-height: 420px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }

  @media (max-width: 900px) {
    height: 55vw;
    max-height: 480px;
    min-height: 260px;
  }
`;

/* Subtle dark-to-transparent gradient on the left edge of the photo
   so it bleeds softly into the charcoal panel */
const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    ${CHARCOAL}44 0%,
    transparent 28%
  );
  pointer-events: none;
`;
